import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import CloseIcon from '@material-ui/icons/Close';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import StyleOutlinedIcon from '@material-ui/icons/StyleOutlined';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import {
	getUser,
	getUserCards,
	getWatchCatalog,
	getCardNameMatches,
	addDiscordUserCardWatch,
	updateDiscordUserCardWatch,
	deleteDiscordUserCardWatch,
	updateDiscordUserSettings
} from './discordCardActions';
import {
	COLLECTIONS,
	buildCatalog,
	groupWatches,
	scopedMatches,
	imageFor,
	mergeWatchSets,
	isSetWatch,
	setMatches as setWatchMatches
} from './watchCatalog';
import MoreWatches from './MoreWatches';
import DeliveryMethods, { deliveryLabel } from './DeliveryMethods';
import './preferences.css';
import tcgdropLogo from '../../assets/tcgdrop-logo.png';
import { COLLECTION_ICONS } from './CollectionIcons';

const ICONS = {
	...COLLECTION_ICONS,
	other: StyleOutlinedIcon
};
const visibleCollections = COLLECTIONS.filter(
	item => !['digimon', 'star-wars-destiny'].includes(item.id)
).sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
const watchName = product => {
	const wholeSet =
		product.sets && product.sets.find(set => set.watch_scope === 'set');
	return (
		(wholeSet && wholeSet.set_name) ||
		product.display_name ||
		product.card_name
	);
};
const titleCase = value => value.charAt(0).toUpperCase() + value.slice(1);
function ProductImage({ product, large = false }) {
	const src = imageFor(product);
	const [failed, setFailed] = useState(false);
	useEffect(() => setFailed(false), [src]);
	const Icon = ICONS[product.collection] || StyleOutlinedIcon;
	return (
		<div
			className={`watch-image ${large ? 'large' : ''} ${
				product.collection === 'lego' ? 'lego-image' : ''
			}`}
		>
			{src && !failed ? (
				<img
					src={src}
					alt={watchName(product)}
					loading="lazy"
					onError={() => setFailed(true)}
				/>
			) : (
				<Icon aria-label="Product image unavailable" />
			)}
		</div>
	);
}

export default function DiscordCards() {
	const guid = new URLSearchParams(window.location.search).get('guid') || '';
	const queryClient = useQueryClient();
	const [collection, setCollection] = useState('magic');
	const [type, setType] = useState('singles');
	const [page, setPage] = useState('watches');
	const [term, setTerm] = useState('');
	const [filter, setFilter] = useState('');
	const [matches, setMatches] = useState([]);
	const [searching, setSearching] = useState(false);
	const [searchError, setSearchError] = useState('');
	const [searchOpen, setSearchOpen] = useState(false);
	const [activeResult, setActiveResult] = useState(0);
	const [panel, setPanel] = useState(null);
	const [panelClosing, setPanelClosing] = useState(false);
	const panelCloseTimer = useRef(null);
	useEffect(() => () => clearTimeout(panelCloseTimer.current), []);
	const [selectedIds, setSelectedIds] = useState([]);
	const [variantLoading, setVariantLoading] = useState(false);
	const [variantError, setVariantError] = useState('');
	const [busy, setBusy] = useState(false);
	const [notice, setNotice] = useState(null);
	const [deleting, setDeleting] = useState(null);
	const searchRef = useRef(null);
	const panelRef = useRef(null);
	const deleteRef = useRef(null);
	const editRequest = useRef(0);
	const watchKey = ['discord_user_cards', guid];
	const userKey = ['discord_user', guid];
	const watches = useQuery(watchKey, () => getUserCards(guid), {
		enabled: !!guid
	});
	const user = useQuery(userKey, () => getUser(guid), { enabled: !!guid });
	const setsQuery = useQuery('watch_catalog', getWatchCatalog, {
		enabled: !!guid
	});
	const rows = Array.isArray(watches.data) ? watches.data : [];
	const catalog = useMemo(() => buildCatalog(setsQuery.data), [
		setsQuery.data
	]);
	const grouped = useMemo(() => groupWatches(rows, catalog), [rows, catalog]);
	const current = COLLECTIONS.find(item => item.id === collection);
	const scopeName = current ? current.short : 'Other';
	const availableWatches = grouped.filter(
		row =>
			row.collection === collection &&
			(collection === 'other' || row.type === type)
	);
	const matchesFilter = set =>
		set.set_name === filter ||
		(catalog.get(String(set.set_id)) || {}).name === filter;
	const filters = Array.from(
		new Set([
			...Array.from(catalog.values())
				.filter(
					set =>
						current && Number(set.tcg_category) === current.category
				)
				.map(set => set.name),
			...availableWatches.flatMap(row =>
				row.sets.map(set => set.set_name)
			)
		])
	).sort();
	const visible = availableWatches
		.filter(row => !filter || row.sets.some(matchesFilter))
		.sort((a, b) =>
			watchName(a).localeCompare(watchName(b), 'en', {
				sensitivity: 'base',
				numeric: true
			})
		);
	const visibleMatches = matches
		.map(product => ({
			...product,
			sets: filter ? product.sets.filter(matchesFilter) : product.sets
		}))
		.filter(product => product.sets.length);
	const limit = Number(user.data && user.data.card_limit);
	const atLimit = Number.isFinite(limit) && rows.length >= limit;
	const dataReady =
		!!watches.data && !!user.data && !watches.error && !user.error;
	const notificationMethod =
		user.data?.notification_method || 'channel_mention';
	const watchingSet = isSetWatch(collection, type);
	const variantLabel = watchingSet
		? collection === 'lego'
			? 'Theme'
			: 'Set'
		: collection === 'lego'
		? 'Sets'
		: 'Editions';
	const isSearchingTerm = term.trim().length >= 3;
	const hasCategoryMetadata = Array.from(catalog.values()).some(
		set => Number(set.tcg_category) !== 1
	);

	useEffect(() => {
		const previous = document.title;
		document.title = 'My watches · TCGDrop';
		document.documentElement.classList.add('preferences-page');
		document.body.classList.add('preferences-page');
		return () => {
			document.title = previous;
			document.documentElement.classList.remove('preferences-page');
			document.body.classList.remove('preferences-page');
		};
	}, []);
	const panelMode = panel && !panelClosing && panel.mode;
	useEffect(() => {
		if (panelMode && panelRef.current)
			panelRef.current.focus({ preventScroll: true });
	}, [panelMode]);
	useEffect(() => {
		if (deleting && deleteRef.current) deleteRef.current.focus();
	}, [deleting]);
	useEffect(() => {
		const media = window.matchMedia('(max-width: 900px)');
		const previous = document.documentElement.style.overflowY;
		const lock = () => {
			document.documentElement.style.overflowY =
				panel && media.matches ? 'hidden' : previous;
		};
		lock();
		media.addListener(lock);
		return () => {
			media.removeListener(lock);
			document.documentElement.style.overflowY = previous;
		};
	}, [panel]);
	useEffect(() => {
		let active = true;
		setMatches([]);
		setSearchError('');
		setActiveResult(0);
		if (!isSearchingTerm || !current) {
			setSearching(false);
			return;
		}
		if (isSetWatch(collection, type)) {
			setMatches(setWatchMatches(catalog, collection, term));
			setSearching(false);
			return;
		}
		setSearching(true);
		const timer = setTimeout(async () => {
			try {
				const results = await getCardNameMatches(
					term.trim(),
					current.category,
					type
				);
				if (active)
					setMatches(
						scopedMatches(results, catalog, collection, type)
					);
			} catch (error) {
				if (active)
					setSearchError('Search is unavailable. Please try again.');
			} finally {
				if (active) setSearching(false);
			}
		}, 300);
		return () => {
			active = false;
			clearTimeout(timer);
		};
	}, [term, collection, type, catalog, isSearchingTerm, current]);

	const closePanel = () => {
		editRequest.current += 1;
		clearTimeout(panelCloseTimer.current);
		if (
			panel &&
			!window.matchMedia('(prefers-reduced-motion: reduce)').matches
		) {
			setPanelClosing(true);
			panelCloseTimer.current = setTimeout(() => {
				setPanel(null);
				setPanelClosing(false);
			}, 240);
		} else {
			setPanel(null);
			setPanelClosing(false);
		}
		setVariantError('');
		setVariantLoading(false);
	};
	const navigate = (id, nextType) => {
		if (busy) return;
		setCollection(id);
		setType(nextType || (id === 'lego' ? 'sets' : 'singles'));
		setPage('watches');
		setTerm('');
		setFilter('');
		setSearchOpen(false);
		closePanel();
	};
	const chooseProduct = product => {
		if (busy) return;
		editRequest.current += 1;
		clearTimeout(panelCloseTimer.current);
		setPanelClosing(false);
		setPanel({ mode: 'add', product, original: null });
		setSelectedIds(product.sets.map(set => String(set.card_id)));
		setVariantError('');
		setVariantLoading(false);
		setSearchOpen(false);
	};
	const openAdd = () => {
		if (busy) return;
		// Begin with product search on every screen size. The editor needs a
		// selected product; chooseProduct opens it once there is one to configure.
		closePanel();
		setSearchOpen(true);
		if (searchRef.current) searchRef.current.focus();
	};
	const edit = async product => {
		const request = ++editRequest.current;
		clearTimeout(panelCloseTimer.current);
		setPanelClosing(false);
		setPanel({ mode: 'edit', product, original: product });
		setSelectedIds(product.sets.map(set => String(set.card_id)));
		setVariantError('');
		const singleSealedItem = product.type === 'sealed';
		setVariantLoading(
			!singleSealedItem && !isSetWatch(product.collection, product.type)
		);
		setTerm('');
		setSearchOpen(false);
		if (singleSealedItem || isSetWatch(product.collection, product.type))
			return;
		try {
			const result = await getCardNameMatches(
				product.card_name,
				current && current.category
			);
			if (request !== editRequest.current) return;
			const options = (
				scopedMatches(
					result,
					catalog,
					product.collection,
					product.type
				).find(item => item.card_name === product.card_name) || {}
			).sets;
			if (!options || !options.length) throw new Error();
			// Retain stored variants missing from the current catalog.
			const variants = new Map(
				product.sets.map(set => [String(set.card_id), set])
			);
			options.forEach(set => variants.set(String(set.card_id), set));
			setPanel({
				mode: 'edit',
				product: { ...product, sets: Array.from(variants.values()) },
				original: product
			});
		} catch (error) {
			if (request === editRequest.current)
				setVariantError(
					'Could not load available editions. Close this panel and try again.'
				);
		} finally {
			if (request === editRequest.current) setVariantLoading(false);
		}
	};
	const saveWatch = async event => {
		event.preventDefault();
		if (
			!panel ||
			!panel.product ||
			busy ||
			variantLoading ||
			variantError ||
			!selectedIds.length
		)
			return;
		const product = panel.product;
		const chosen = product.sets.filter(set =>
			selectedIds.includes(String(set.card_id))
		);
		const existing = rows.find(row => row.card_name === product.card_name);
		if (!existing && atLimit) {
			setNotice({
				error: true,
				text: 'You have reached your watch limit.'
			});
			return;
		}
		const merged = mergeWatchSets(
			rows,
			product.card_name,
			chosen,
			panel.original ? panel.original.sets : []
		);
		setBusy(true);
		try {
			await (existing
				? updateDiscordUserCardWatch
				: addDiscordUserCardWatch)(guid, product.card_name, merged);
			queryClient.setQueryData(watchKey, old => {
				const previous = Array.isArray(old) ? old : [];
				return (existing
					? previous.map(row =>
							row.card_name === product.card_name
								? { ...row, sets: merged }
								: row
					  )
					: [
							...previous,
							{ card_name: product.card_name, sets: merged }
					  ]
				).sort((a, b) => a.card_name.localeCompare(b.card_name));
			});
			setNotice({
				text: `${watchName(product)} ${
					panel.mode === 'edit' || existing
						? 'updated'
						: 'added to your watches'
				}.`
			});
			closePanel();
			setTerm('');
			queryClient.invalidateQueries(watchKey);
		} catch (error) {
			setNotice({
				error: true,
				text: error.message || 'Could not save this watch.'
			});
		} finally {
			setBusy(false);
		}
	};
	const removeWatch = async () => {
		if (!deleting || busy) return;
		const product = deleting;
		const remaining = mergeWatchSets(
			rows,
			product.card_name,
			[],
			product.sets
		);
		setBusy(true);
		try {
			await (remaining.length
				? updateDiscordUserCardWatch(guid, product.card_name, remaining)
				: deleteDiscordUserCardWatch(guid, product.card_name));
			queryClient.setQueryData(watchKey, old =>
				remaining.length
					? old.map(row =>
							row.card_name === product.card_name
								? { ...row, sets: remaining }
								: row
					  )
					: old.filter(row => row.card_name !== product.card_name)
			);
			setDeleting(null);
			closePanel();
			setNotice({
				text: `${watchName(product)} removed from this collection.`
			});
			queryClient.invalidateQueries(watchKey);
		} catch (error) {
			setNotice({ error: true, text: error.message });
		} finally {
			setBusy(false);
		}
	};
	const saveSettings = async (field, value) => {
		if (busy || !dataReady) return;
		const previous = user.data;
		const next = { ...previous, [field]: value };
		setBusy(true);
		queryClient.setQueryData(userKey, next);
		try {
			await updateDiscordUserSettings(guid, next);
			setNotice({ text: 'Notification settings saved.' });
			queryClient.invalidateQueries(userKey);
		} catch (error) {
			queryClient.setQueryData(userKey, previous);
			setNotice({ error: true, text: error.message });
		} finally {
			setBusy(false);
		}
	};
	const panelProduct = panel && panel.product;
	const allSelected =
		!!panelProduct &&
		panelProduct.sets.every(set =>
			selectedIds.includes(String(set.card_id))
		);
	const toggleVariant = id =>
		setSelectedIds(ids =>
			ids.includes(String(id))
				? ids.filter(value => value !== String(id))
				: [...ids, String(id)]
		);

	return (
		<div
			className={`preferences-app ${
				guid && page === 'watches' ? 'watches-page' : ''
			}`}
		>
			<header className="preferences-header">
				<Link
					to="/search"
					className="tcgdrop-wordmark"
					aria-label="TCGDrop home"
				>
					<img
						src={tcgdropLogo}
						alt="TCGDrop"
						width="59"
						height="56"
					/>
				</Link>
				<nav aria-label="Preferences">
					<button
						disabled={busy}
						className={page === 'watches' ? 'active' : ''}
						onClick={() => setPage('watches')}
					>
						My watches
					</button>
					<button
						disabled={busy}
						className={page === 'settings' ? 'active' : ''}
						onClick={() => {
							if (!busy) {
								setPage('settings');
								closePanel();
							}
						}}
					>
						Notification settings
					</button>
				</nav>
				<div className="preferences-account">
					<i className="fab fa-discord" aria-hidden="true" />
					<span>
						{user.data ? user.data.username : 'Discord watches'}
					</span>
				</div>
			</header>
			<div className="preferences-layout">
				<aside className="collections-nav" aria-label="Collections">
					<h2>Collections</h2>
					<label className="mobile-collection-picker">
						<span>Collection</span>
						<select
							aria-label="Collection"
							value={collection}
							disabled={busy}
							onChange={event => navigate(event.target.value)}
						>
							{visibleCollections.map(item => (
								<option key={item.id} value={item.id}>
									{item.name} (
									{
										grouped.filter(
											row => row.collection === item.id
										).length
									}
									)
								</option>
							))}
							{grouped.some(
								row => row.collection === 'other'
							) && <option value="other">Other watches</option>}
						</select>
					</label>
					{visibleCollections.map(item => {
						const Icon = ICONS[item.id] || StyleOutlinedIcon;
						const count = grouped.filter(
							row => row.collection === item.id
						).length;
						const active = item.id === collection;
						return (
							<div
								className={`collection-group ${
									active ? 'selected' : ''
								}`}
								key={item.id}
							>
								<button
									className="collection-button"
									disabled={busy}
									aria-expanded={active}
									onClick={() => navigate(item.id)}
								>
									<Icon
										className={`category-icon ${item.id}`}
									/>
									<span>{item.name}</span>
									<span className="collection-count">
										{count}
									</span>
									{active ? (
										<ExpandMoreIcon />
									) : (
										<ChevronRightIcon />
									)}
								</button>
								{active && (
									<div className="collection-types">
										{item.types.map(kind => (
											<button
												key={kind}
												disabled={busy}
												aria-current={
													type === kind &&
													page === 'watches'
														? 'page'
														: undefined
												}
												className={
													type === kind
														? 'active'
														: ''
												}
												onClick={() =>
													navigate(item.id, kind)
												}
											>
												{kind === 'more'
													? 'Groups'
													: titleCase(kind)}
												<span>
													{
														grouped.filter(
															row =>
																row.collection ===
																	item.id &&
																row.type ===
																	kind
														).length
													}
												</span>
											</button>
										))}
									</div>
								)}
							</div>
						);
					})}
					{grouped.some(row => row.collection === 'other') && (
						<button
							className={`other-collection ${
								collection === 'other' ? 'active' : ''
							}`}
							disabled={busy}
							onClick={() => navigate('other')}
						>
							Other watches{' '}
							<span>
								{
									grouped.filter(
										row => row.collection === 'other'
									).length
								}
							</span>
						</button>
					)}
				</aside>
				{!guid ? (
					<main className="preferences-main">
						<h1>Open your personal watch link</h1>
						<p className="muted">
							Use the preferences link provided by the Discord bot
							to manage your watches.
						</p>
					</main>
				) : (
					<main
						className={`preferences-main ${
							panel && page === 'watches' ? 'with-panel' : ''
						}`}
					>
						{notice && (
							<div
								className={`preferences-notice ${
									notice.error ? 'error' : ''
								}`}
								role={notice.error ? 'alert' : 'status'}
							>
								<span>{notice.text}</span>
								<button
									aria-label="Dismiss message"
									onClick={() => setNotice(null)}
								>
									<CloseIcon />
								</button>
							</div>
						)}
						{(watches.error || user.error) && (
							<div className="load-error" role="alert">
								<strong>
									We couldn’t load your preferences.
								</strong>
								<p>
									Check your connection and personal link,
									then try again.
								</p>
								<button
									className="secondary-button"
									onClick={() => {
										watches.refetch();
										user.refetch();
									}}
								>
									Try again
								</button>
							</div>
						)}
						{page === 'settings' ? (
							<section className="settings-page">
								<h1>Notification settings</h1>
								<p className="page-subtitle">
									Choose which listings you want to hear
									about.
								</p>
								<DeliveryMethods
									method={notificationMethod}
									dmAvailable={
										!!user.data?.discord_dm_available
									}
									disabled={busy || !dataReady}
									onChange={value =>
										saveSettings(
											'notification_method',
											value
										)
									}
								/>
								<fieldset
									className="settings-section"
									disabled={busy || !dataReady}
								>
									<legend>Listing sources</legend>
									{[
										[
											'bin_alerts',
											'eBay Buy It Now',
											'New fixed-price listings'
										],
										[
											'auction_alerts',
											'eBay Auctions',
											'Auctions for your watched items'
										],
										[
											'tcg_alerts',
											'TCGPlayer listings',
											'Matches from TCGPlayer'
										]
									].map(([field, label, description]) => (
										<label
											className="setting-checkbox"
											key={field}
										>
											<input
												type="checkbox"
												checked={
													Number(
														user.data &&
															user.data[field]
													) === 1
												}
												onChange={event =>
													saveSettings(
														field,
														event.target.checked
															? 1
															: 0
													)
												}
											/>
											<span>
												<strong>{label}</strong>
												<small>{description}</small>
											</span>
										</label>
									))}
								</fieldset>
								<div className="settings-section">
									<label
										className="field-label"
										htmlFor="listing-country"
									>
										Listing country
									</label>
									<p className="muted">
										Only receive notifications for listings
										from this country.
									</p>
									<select
										id="listing-country"
										value={
											(user.data &&
												user.data.listing_origin) ||
											'null'
										}
										disabled={busy || !dataReady}
										onChange={event =>
											saveSettings(
												'listing_origin',
												event.target.value
											)
										}
									>
										{[
											['null', 'Any country'],
											['US', 'United States'],
											['CA', 'Canada'],
											['AU', 'Australia'],
											['GB', 'United Kingdom']
										].map(([value, label]) => (
											<option value={value} key={value}>
												{label}
											</option>
										))}
									</select>
								</div>
								<p className="autosave">
									<CheckCircleOutlineIcon />
									{busy
										? 'Saving your settings…'
										: 'Changes are saved automatically.'}
								</p>
							</section>
						) : type === 'more' && current ? (
							<MoreWatches
								guid={guid}
								collection={current}
								rows={rows}
								busy={busy}
								atLimit={atLimit}
								ready={dataReady}
								onBusy={setBusy}
								onNotice={setNotice}
							/>
						) : (
							<>
								<div className="watch-title-row">
									<div>
										<h1>{scopeName} watches</h1>
										<p className="page-subtitle">
											{watchingSet
												? 'Watch every matching item in a set or theme. '
												: ''}
											Get notified when the{' '}
											{collection === 'lego'
												? 'sets'
												: type === 'sealed'
												? 'products'
												: 'cards'}{' '}
											you care about match.
										</p>
									</div>
									{current && (
										<button
											className="primary-button add-watch"
											disabled={
												busy || !dataReady || atLimit
											}
											onClick={openAdd}
										>
											<AddIcon />
											Add watch
										</button>
									)}
								</div>
								{current && (
									<div className="search-toolbar">
										<span className="search-scope">
											<span>
												{scopeName} · {titleCase(type)}
											</span>
										</span>
										<div
											className="product-search"
											onBlur={event => {
												if (
													!event.currentTarget.contains(
														event.relatedTarget
													)
												)
													setSearchOpen(false);
											}}
										>
											<SearchIcon />
											<input
												ref={searchRef}
												disabled={busy}
												value={term}
												role="combobox"
												aria-expanded={
													searchOpen &&
													isSearchingTerm
												}
												aria-controls="watch-search-results"
												aria-autocomplete="list"
												aria-activedescendant={
													searchOpen &&
													visibleMatches.length
														? `watch-result-${activeResult}`
														: undefined
												}
												aria-label={`Search ${scopeName} ${type}`}
												placeholder={
													watchingSet
														? `Find a ${
																collection ===
																'lego'
																	? 'LEGO theme'
																	: scopeName +
																	  ' set'
														  }…`
														: collection === 'lego'
														? 'Find a LEGO set or number…'
														: `Find a ${scopeName} ${
																type ===
																'sealed'
																	? 'product'
																	: 'card'
														  }…`
												}
												onFocus={() =>
													setSearchOpen(true)
												}
												onChange={event => {
													setTerm(event.target.value);
													setSearchOpen(true);
												}}
												onKeyDown={event => {
													if (
														event.key === 'Escape'
													) {
														setSearchOpen(false);
														return;
													}
													if (
														event.key ===
															'ArrowDown' ||
														event.key === 'ArrowUp'
													) {
														event.preventDefault();
														setSearchOpen(true);
														setActiveResult(index =>
															visibleMatches.length
																? (index +
																		(event.key ===
																		'ArrowDown'
																			? 1
																			: visibleMatches.length -
																			  1)) %
																  visibleMatches.length
																: 0
														);
													}
													if (
														event.key === 'Enter' &&
														searchOpen &&
														visibleMatches[
															activeResult
														]
													) {
														event.preventDefault();
														chooseProduct(
															visibleMatches[
																activeResult
															]
														);
													}
												}}
											/>
											{term && (
												<button
													aria-label="Clear search"
													onClick={() => {
														setTerm('');
														searchRef.current.focus();
													}}
												>
													<CloseIcon />
												</button>
											)}
											{searchOpen && isSearchingTerm && (
												<div
													className="search-results"
													id="watch-search-results"
													role="listbox"
													aria-label="Matching products"
												>
													{searching ? (
														<p role="status">
															Searching{' '}
															{scopeName}…
														</p>
													) : searchError ? (
														<p role="alert">
															{searchError}
														</p>
													) : !visibleMatches.length ? (
														<p>
															No matches in{' '}
															{scopeName} {type}.
															Try another name
															{collection ===
															'lego'
																? ''
																: ' or product type'}
															.
														</p>
													) : (
														visibleMatches.map(
															(
																product,
																index
															) => (
																<button
																	type="button"
																	role="option"
																	aria-selected={
																		index ===
																		activeResult
																	}
																	id={`watch-result-${index}`}
																	key={
																		product.card_name
																	}
																	className={
																		index ===
																		activeResult
																			? 'highlighted'
																			: ''
																	}
																	onMouseDown={event =>
																		event.preventDefault()
																	}
																	onClick={() =>
																		chooseProduct(
																			product
																		)
																	}
																>
																	<ProductImage
																		product={
																			product
																		}
																	/>
																	<span>
																		<strong>
																			{watchName(
																				product
																			)}
																		</strong>
																		<small>
																			{Array.from(
																				new Set(
																					product.sets.map(
																						set =>
																							set.set_name
																					)
																				)
																			)
																				.slice(
																					0,
																					2
																				)
																				.join(
																					' · '
																				)}
																			{product
																				.sets
																				.length >
																			2
																				? ` · ${product.sets.length} variants`
																				: ''}
																		</small>
																	</span>
																	<ChevronRightIcon />
																</button>
															)
														)
													)}
												</div>
											)}
										</div>
										<select
											aria-label={`Filter watched ${variantLabel.toLowerCase()}`}
											value={filter}
											onChange={event => {
												setFilter(event.target.value);
												setActiveResult(0);
											}}
										>
											<option value="">
												{collection === 'lego'
													? 'All themes'
													: 'All editions'}
											</option>
											{filters.map(name => (
												<option key={name} value={name}>
													{name}
												</option>
											))}
										</select>
									</div>
								)}
								{setsQuery.error && (
									<div
										className="catalog-warning"
										role="status"
									>
										Collection details are unavailable.{' '}
										<button
											onClick={() => setsQuery.refetch()}
										>
											Try again
										</button>
									</div>
								)}
								{collection === 'pokemon' &&
									!hasCategoryMetadata &&
									!setsQuery.isLoading && (
										<p className="muted catalog-warning">
											Pokémon search will be available
											when the catalog update is
											connected. Existing uncategorized
											items are kept in Other watches.
										</p>
									)}
								<div className="watch-list-summary">
									<strong>
										{visible.length}{' '}
										{visible.length === 1
											? 'watch'
											: 'watches'}
									</strong>
									{Number.isFinite(limit) && (
										<span>
											{rows.length} of {limit} watches
											used
										</span>
									)}
								</div>
								{watches.isLoading || setsQuery.isLoading ? (
									<div
										className="empty-watches"
										role="status"
									>
										Loading your watches…
									</div>
								) : (
									<>
										{visible.length ? (
											<div
												className={`watch-table ${
													type === 'sealed' ||
													isSetWatch(collection, type)
														? 'watch-table-without-editions'
														: ''
												}`}
											>
												<div className="watch-table-heading">
													<span>
														{collection === 'lego'
															? 'Set'
															: type === 'sealed'
															? 'Product'
															: 'Card'}
													</span>
													<span>
														{collection === 'lego'
															? 'Theme'
															: 'Editions'}
													</span>
													<span>Alert</span>
													<span className="sr-only">
														Actions
													</span>
												</div>
												<div
													className="watch-table-items"
													tabIndex={0}
													aria-label="Watched items"
												>
													{visible.map(product => (
														<div
															className="watch-row"
															key={
																product.collection +
																product.type +
																product.card_name +
																(product.type ===
																'sealed'
																	? ':' +
																	  product
																			.sets[0]
																			.card_id
																	: '')
															}
														>
															<button
																type="button"
																className="watch-row-open"
																aria-label={`Open watch editor for ${watchName(
																	product
																)}`}
																disabled={busy}
																onClick={() =>
																	edit(
																		product
																	)
																}
															/>
															<div className="watch-product">
																<ProductImage
																	product={
																		product
																	}
																/>
																<div>
																	<strong>
																		{watchName(
																			product
																		)}
																	</strong>
																	<small
																		className={
																			collection ===
																				'lego' &&
																			product
																				.sets[0] &&
																			product
																				.sets[0]
																				.number
																				? 'watch-set-number'
																				: 'watch-collection-type'
																		}
																	>
																		{collection ===
																			'lego' &&
																		product
																			.sets[0] &&
																		product
																			.sets[0]
																			.number
																			? `Set #${
																					product
																						.sets[0]
																						.number
																			  }${
																					product
																						.sets
																						.length >
																					1
																						? ` + ${product
																								.sets
																								.length -
																								1} more`
																						: ''
																			  }`
																			: scopeName +
																			  ' · ' +
																			  titleCase(
																					product.type
																			  )}
																	</small>
																	<div className="watch-editions-inline">
																		{Array.from(
																			new Set(
																				product.sets.map(
																					set =>
																						set.set_name
																				)
																			)
																		).join(
																			', '
																		) ||
																			'Saved selection'}
																	</div>
																</div>
															</div>
															<div className="watch-editions">
																{Array.from(
																	new Set(
																		product.sets.map(
																			set =>
																				set.set_name
																		)
																	)
																).join(', ') ||
																	'Saved selection'}
															</div>
															<div className="watch-alert">
																<NotificationsNoneIcon />
																<span>
																	{notificationMethod ===
																	'discord_dm'
																		? 'Discord DM'
																		: 'Channel mention'}
																</span>
															</div>
															<div className="watch-actions">
																<button
																	aria-label={`Edit ${watchName(
																		product
																	)}`}
																	title="Edit watch"
																	disabled={
																		busy
																	}
																	onClick={() =>
																		edit(
																			product
																		)
																	}
																>
																	<EditOutlinedIcon />
																</button>
																<button
																	aria-label={`Remove ${watchName(
																		product
																	)}`}
																	title="Remove watch"
																	disabled={
																		busy
																	}
																	onClick={() =>
																		setDeleting(
																			product
																		)
																	}
																>
																	<DeleteOutlineIcon />
																</button>
															</div>
														</div>
													))}
												</div>
											</div>
										) : (
											!watches.error && (
												<div className="empty-watches">
													<NotificationsNoneIcon />
													<h2>
														{filter
															? 'No watches in this selection'
															: `No ${scopeName} ${
																	type ===
																	'sealed'
																		? 'sealed '
																		: ''
															  }watches yet`}
													</h2>
													<p>
														{filter
															? 'Choose another edition to see your watches.'
															: current
															? `Search ${scopeName} above to add your first watch.`
															: 'All your watches have a collection.'}
													</p>
													{filter && (
														<button
															className="secondary-button"
															onClick={() =>
																setFilter('')
															}
														>
															Clear filter
														</button>
													)}
												</div>
											)
										)}
									</>
								)}
								{atLimit && (
									<p className="limit-note">
										You’ve reached your limit of {limit}{' '}
										watches. Edit or remove an existing
										watch to make room.
									</p>
								)}
								<footer className="watch-footer">
									<button
										onClick={() => {
											setPage('settings');
											closePanel();
										}}
									>
										Listing sources and countries{' '}
										<ChevronRightIcon />
									</button>
								</footer>
							</>
						)}
					</main>
				)}
				{panel && page === 'watches' && (
					<div
						className={`watch-editor-shell ${
							panelClosing ? 'is-closing' : ''
						}`}
						aria-hidden={panelClosing || undefined}
						inert={panelClosing ? '' : undefined}
					>
						<aside
							className="watch-editor"
							onKeyDown={event => {
								if (event.key === 'Escape' && !busy)
									closePanel();
								if (
									event.key === 'Tab' &&
									window.matchMedia('(max-width: 900px)')
										.matches
								) {
									const controls = Array.from(
										event.currentTarget.querySelectorAll(
											'button:not(:disabled), input:not(:disabled)'
										)
									);
									if (
										event.shiftKey &&
										document.activeElement === controls[0]
									) {
										event.preventDefault();
										controls[controls.length - 1].focus();
									} else if (
										!event.shiftKey &&
										document.activeElement ===
											controls[controls.length - 1]
									) {
										event.preventDefault();
										controls[0].focus();
									}
								}
							}}
							aria-label={
								panel.mode === 'edit'
									? 'Edit watch'
									: `Add a ${scopeName} watch`
							}
						>
							<div className="editor-heading">
								<h2 ref={panelRef} tabIndex={-1}>
									{panel.mode === 'edit'
										? 'Edit watch'
										: `Add a ${scopeName} watch`}
								</h2>
								<button
									aria-label="Close watch editor"
									disabled={busy}
									onClick={closePanel}
								>
									<CloseIcon />
								</button>
							</div>
							{!panelProduct ? (
								<div className="editor-empty">
									<SearchIcon />
									<h3>Find your next watch</h3>
									<p>
										{watchingSet
											? `Search ${scopeName} ${type} and select a ${
													collection === 'lego'
														? 'theme'
														: 'set'
											  } to watch every matching item.`
											: `Search ${scopeName} ${type} and select a product to choose ${
													collection === 'lego'
														? 'the sets'
														: 'its editions'
											  }.`}
									</p>
									<button
										className="secondary-button"
										onClick={() =>
											searchRef.current &&
											searchRef.current.focus()
										}
									>
										Search {scopeName}
									</button>
								</div>
							) : (
								<form onSubmit={saveWatch}>
									{/* Scroll the fields independently so the submit action stays visible. */}
									<div className="editor-fields">
										<div className="editor-product">
											<ProductImage
												product={panelProduct}
												large
											/>
											<div>
												<h3>
													{watchName(panelProduct)}
												</h3>
												<p>
													{panelProduct.sets[0] &&
														panelProduct.sets[0]
															.set_name}
												</p>
											</div>
										</div>
										<fieldset
											className="editor-section variant-selection"
											disabled={
												busy ||
												variantLoading ||
												!!variantError
											}
										>
											<legend>{variantLabel}</legend>
											{isSetWatch(
												panelProduct.collection,
												panelProduct.type
											) ? (
												<p className="field-help">
													Watch all matching items in
													this{' '}
													{panelProduct.collection ===
													'lego'
														? 'theme'
														: 'set'}
													, including products added
													later. Your listing and
													delivery preferences still
													apply.
												</p>
											) : (
												<>
													<label className="select-all">
														<input
															type="checkbox"
															checked={
																allSelected
															}
															ref={input => {
																if (input)
																	input.indeterminate =
																		selectedIds.length >
																			0 &&
																		!allSelected;
															}}
															onChange={() =>
																setSelectedIds(
																	allSelected
																		? []
																		: panelProduct.sets.map(
																				set =>
																					String(
																						set.card_id
																					)
																		  )
																)
															}
														/>
														<span>
															{collection ===
															'lego'
																? 'Select all sets'
																: 'Select all editions'}
														</span>
														<small className="selection-count">
															{selectedIds.length}
															/
															{
																panelProduct
																	.sets.length
															}
														</small>
													</label>
													<p className="field-help">
														{collection === 'lego'
															? 'Select the set numbers you want to watch.'
															: 'Select one or more editions to watch.'}
													</p>
													{variantLoading ? (
														<p role="status">
															Loading available
															editions…
														</p>
													) : variantError ? (
														<p
															role="alert"
															className="inline-error"
														>
															{variantError}
														</p>
													) : (
														<div className="variant-options">
															{panelProduct.sets.map(
																set => (
																	<label
																		className={
																			selectedIds.includes(
																				String(
																					set.card_id
																				)
																			)
																				? 'selected-variant'
																				: ''
																		}
																		key={
																			set.card_id
																		}
																	>
																		<input
																			type="checkbox"
																			checked={selectedIds.includes(
																				String(
																					set.card_id
																				)
																			)}
																			onChange={() =>
																				toggleVariant(
																					set.card_id
																				)
																			}
																		/>
																		<span className="variant-details">
																			<span className="variant-name">
																				{
																					set.set_name
																				}
																			</span>
																			{set.number ? (
																				<small>
																					{collection ===
																					'lego'
																						? 'Set #'
																						: '#'}
																					{
																						set.number
																					}
																				</small>
																			) : null}
																		</span>
																	</label>
																)
															)}
														</div>
													)}
												</>
											)}
										</fieldset>
										<div className="editor-section">
											<h3>Delivery method</h3>
											<div className="editor-delivery">
												<i
													className="fab fa-discord"
													aria-hidden="true"
												/>
												<strong>
													{deliveryLabel(
														notificationMethod
													)}
												</strong>
											</div>
											<p className="field-help">
												All watches use your saved
												notification settings.
											</p>
										</div>
										{!selectedIds.length && (
											<p className="field-help">
												Select at least one{' '}
												{collection === 'lego'
													? 'set'
													: 'edition'}{' '}
												to continue.
											</p>
										)}
									</div>
									<div className="editor-actions">
										<button
											className="primary-button save-watch"
											type="submit"
											disabled={
												busy ||
												variantLoading ||
												!!variantError ||
												!selectedIds.length ||
												!dataReady ||
												(atLimit &&
													!rows.some(
														row =>
															row.card_name ===
															panelProduct.card_name
													))
											}
										>
											{busy
												? 'Saving…'
												: panel.mode === 'edit'
												? 'Save changes'
												: 'Create watch'}
										</button>
									</div>
								</form>
							)}
						</aside>
					</div>
				)}
			</div>
			{deleting && (
				<div
					className="modal-scrim"
					onClick={event => {
						if (!busy && event.target === event.currentTarget)
							setDeleting(null);
					}}
				>
					<section
						className="remove-dialog"
						role="dialog"
						aria-modal="true"
						aria-labelledby="remove-title"
						onKeyDown={event => {
							if (event.key === 'Escape' && !busy)
								setDeleting(null);
							if (event.key === 'Tab') {
								const buttons = Array.from(
									event.currentTarget.querySelectorAll(
										'button:not(:disabled)'
									)
								);
								if (
									event.shiftKey &&
									document.activeElement === buttons[0]
								) {
									event.preventDefault();
									buttons[buttons.length - 1].focus();
								} else if (
									!event.shiftKey &&
									document.activeElement ===
										buttons[buttons.length - 1]
								) {
									event.preventDefault();
									buttons[0].focus();
								}
							}
						}}
					>
						<h2 id="remove-title">Remove this watch?</h2>
						<p>
							<strong>{watchName(deleting)}</strong> will be
							removed from your {scopeName} {type} watches.
						</p>
						<div>
							<button
								ref={deleteRef}
								className="secondary-button"
								disabled={busy}
								onClick={() => setDeleting(null)}
							>
								Keep watch
							</button>
							<button
								className="primary-button"
								disabled={busy}
								onClick={removeWatch}
							>
								{busy ? 'Removing…' : 'Remove watch'}
							</button>
						</div>
					</section>
				</div>
			)}
		</div>
	);
}
